import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './PWAPrompt.css';

const PWAPrompt = () => {
    const [showPrompt, setShowPrompt] = useState(false);
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [isIOS, setIsIOS] = useState(false);
    const { t } = useTranslation();

    useEffect(() => {
        // Check if the user has already dismissed or installed the PWA
        const hasInteractedWithPWA = localStorage.getItem('pwaInteraction') === 'true';

        // Check if it's iOS
        const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent)
            || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
        setIsIOS(isIOSDevice);

        // Check if the app is already installed
        const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                           window.navigator.standalone || 
                           document.referrer.includes('android-app://');

        if (isStandalone) {
            localStorage.setItem('pwaInteraction', 'true');
            return;
        }

        if (hasInteractedWithPWA) return;

        const handler = (e) => {
            // Prevent the default prompt
            e.preventDefault();
            // Store the event for later use
            setDeferredPrompt(e);
            // Show our custom prompt
            setShowPrompt(true);
        };

        window.addEventListener('beforeinstallprompt', handler);

        // Show prompt for iOS even without the install event
        if (isIOSDevice) {
            setShowPrompt(true);
        }

        // Listen for successful installation
        const handleAppInstalled = () => {
            localStorage.setItem('pwaInteraction', 'true');
            setDeferredPrompt(null);
            setShowPrompt(false);
        };
        window.addEventListener('appinstalled', handleAppInstalled);

        return () => {
            window.removeEventListener('beforeinstallprompt', handler);
            window.removeEventListener('appinstalled', handleAppInstalled);
        };
    }, []);

    const handleInstallClick = async () => {
        if (isIOS) {
            // For iOS, we just close the prompt as they need to use the manual method
            setShowPrompt(false);
            localStorage.setItem('pwaInteraction', 'true');
            return;
        }

        if (deferredPrompt) {
            try {
                // Show the install prompt
                await deferredPrompt.prompt();
                // Wait for the user to respond to the prompt
                const { outcome } = await deferredPrompt.userChoice;
                
                // Clear the deferredPrompt variable
                setDeferredPrompt(null);
                // Hide the prompt
                setShowPrompt(false);
                // Do not ask again after an accepted installation or an explicit dismissal.
                if (outcome === 'accepted' || outcome === 'dismissed') {
                    localStorage.setItem('pwaInteraction', 'true');
                }
            } catch (error) {
                console.error('Error during PWA installation:', error);
            }
        }
    };

    const handleClose = () => {
        setShowPrompt(false);
        localStorage.setItem('pwaInteraction', 'true');
    };

    if (!showPrompt) return null;

    return (
        <div className="pwa-prompt">
            <div className="pwa-prompt-content">
                <img 
                    src={require('../../assets/images/logo.png')} 
                    alt="Trakna"
                    className="pwa-prompt-icon"
                />
                <h3>{t('pwa.title')}</h3>
                {isIOS ? (
                    <p>
                        {t('pwa.messageIOS', {
                            defaultValue: "Pour installer l'application : appuyez sur l'icône de partage puis 'Sur l'écran d'accueil'"
                        })}
                    </p>
                ) : (
                    <p>{t('pwa.message')}</p>
                )}
                <div className="pwa-prompt-buttons">
                    <button 
                        onClick={handleInstallClick} 
                        className="pwa-install-button"
                    >
                        {isIOS ? t('pwa.got_it') : t('pwa.install')}
                    </button>
                    <button 
                        onClick={handleClose}
                        className="pwa-close-button"
                    >
                        {t('pwa.notNow')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PWAPrompt;
